import { useEffect, useState } from "react";
import AppHeader from "@/layouts/AppHeader";
import { Helmet } from "react-helmet-async";
import HomeFilterBar from "@/features/HomeFilterBar";
import ArrowNav from "@/components/ArrowNav";
import { Button } from "@/components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import ModelCard from "@/components/ModelCard";
import TileCard from "@/components/TileCard";
import ChatbaseScript from "@/components/ChatbaseScript";
import Sitemap from "@/components/Sitemap";
import OfferCard from "@/components/OfferCard";
import Award from "@/components/Award";
import { getBanners } from "@/lib/service";
import { BannerProps } from "@/lib/type";
import { AppFooter } from "@/layouts/AppFooter";
import { useRegion } from "@/context/RegionContext";

const AI: React.FC = () => {
  const [mobileShow, setMobileShow] = useState(false);
  const [banners, setBanners] = useState<BannerProps[]>([]);
  const navigate = useNavigate();
  const { region } = useRegion();
  const handleNavigate = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    getBannerList();
  }, []);

  const getBannerList = async () => {
    const bannerList = await getBanners("all");
    setBanners(bannerList.data);
  };

  return (
    <>
      <ChatbaseScript />
      <Helmet>
        <title>Europcar {region.countryName}: AI-Powered Car Rental Experience</title>
        <meta name="description" content="Experience the future of car rental with our AI-powered recommendations and personalized service. Find the perfect vehicle for your needs with smart technology." />
        <meta name="keywords" content={`ai car rental, smart rental, personalized car rental, ${region.seoKeywords}`} data-hid="keywords"/>
      </Helmet>

      {/* Header */}
      <div className="bg-black bg-[url('/home/background.webp')] bg-[length:550px_250px] bg-no-repeat bg-[center_top_0rem]">
        <AppHeader className="pt-4 px-4 md:px-32" />
        <div className="px-4 md:px-32">
          <div className="md:w-2/5 text-white font-bold md:text-3xl text-xl mt-4 md:mb-10 mb-4">
            Looking for a vehicle? You're at the right place.
          </div>
          <HomeFilterBar />
          <div className="grid grid-rows-4 space-y-4 md:space-y-0 md:grid-cols-4 py-6">
            <ArrowNav title="My bookings" onClick={() => { navigate(`/search-booking`)}} />
            <ArrowNav title="Members' Discount" onClick={() => { handleNavigate(`https://privilege.europcar.com/join/`);}} />
            <ArrowNav title="See all our deals" onClick={() => { navigate(`/pages/top-deals`);}} />
            <ArrowNav title="Go to Europcar Business" onClick={() => { navigate(`/pages/corporate-car-leasing/`);}} />
          </div>
        </div>
      </div>

      {/* Offer */}
      <div className="md:mx-32 mx-4 my-12">
        <p className="font-bold text-xl mb-8 md:mx-0 mx-4">
          Today's car rental offers
        </p>
        <div className="grid grid-row-4 md:grid-cols-4 gap-5 mb-6">
          {banners.slice(0, 4).map((e) => (
            <OfferCard
              key={`banner-${e.id}`}
              bgImage={e.image_path || ''}
              title={e.main_title}
              descTop={e.subtitle}
              url={e.redirect_url}
              isRedirect={e.open_in_new_tab == 1}
            />
          ))}
        </div>
        <div className="flex md:flex-row flex-col md:space-x-6 mt-2 md:mt-0">
          <img
            src="/home/ios_app.jpeg"
            className="md:w-3/5 w-full rounded-sm"
          />
          <div className="md:w-2/5 md:my-auto mt-2 space-y-2 md:justify-items-start justify-items-center">
            <p className="font-bold text-gray-500 text-sm">
              {region.homepageContent.leasing.eyebrow}
            </p>
            <p className="font-bold text-xl">Leasing a car that easy way</p>
            <p className="text-gray-400 md:text-start text-center">
              {region.homepageContent.leasing.description}
            </p>
            <Button className="bg-yellow-400 text-black font-medium py-6">
              <a href={`${region.productionUrl}/pages/corporate-car-leasing`}>More information</a>
            </Button>
          </div>
        </div>
      </div>

      {/* Fleet */}
      <div className="relative">
        <div className="bg-[#f7f7f7] md:px-32 px-4 pt-8 md:pb-96 mb-12">
          <p className="font-bold text-xl">A fleet that meets your needs</p>
          <p className="text-gray-400">
            Take the opportunity to test the new models
          </p>
        </div>
        <div className="md:mx-52 mx-4 grid md:grid-cols-3 md:gap-5 md:absolute md:-bottom-4">
          <ModelCard
            title="Sedans"
            desc="Explore our range of comfortable and fuel-efficient sedans, perfect for city driving or long-distance travel."
            image="/home/bezza.png"
            link={`${region.productionUrl}/pages/economy-car`}
          />
          <ModelCard
            title="SUVs"
            desc="Discover our versatile SUVs with ample space and power, ideal for families and adventurous journeys"
            image="/home/x-trail.png"
            link={`${region.productionUrl}/pages/premium-car`}
          />
          <ModelCard
            title="MPVs"
            desc="Choose from our selection of spacious MPVs, offering great seating capacity and comfort for group travel."
            image="/home/innova-2024.png"
            link={`${region.productionUrl}/pages/family-car`}
          />
        </div>
      </div>

      {/* Discover */}
      <div className="md:mx-32 mx-4 grid grid-row-3 mt-10 md:mt-0 md:grid-cols-3 gap-4 mb-6">
        <TileCard
          image="/icon/discount.svg"
          title="Discounts and Benefits"
          desc="Become a Privilege Member."
          buttonDesc="Discover"
          link="https://privilege.europcar.com/join/"
        />
        <TileCard
          image="/icon/periodic.svg"
          title="Flexible Rental Period"
          desc="Whether you need daily, monthly or yearly choose a length."
          buttonDesc="Book now"
          link={`${region.productionUrl}/pages/corporate-car-leasing`}
        />
        <TileCard
          image="/icon/icone-online-checkin.svg"
          title="Best Price Guaranteed"
          desc="Get the best price when you book direct."
          buttonDesc="Read more"
          link={`${region.productionUrl}/pages/top-deals`}
        />
      </div>

      {/* Car Rental Location */}
      <div className="bg-[#f7f7f7] md:px-32 px-4 py-8 flex md:flex-row flex-col">
        <div className="mr-12">
          <img src="/icon/globe.svg" />
          <p className="font-bold text-2xl">
            {region.homepageHeading}
          </p>
          <p className="text-gray-500 text-xl">
            <span className="text-green-600">3835</span> Europcar locations in
            over <span className="text-green-600">140</span> countries
          </p>
        </div>
        <div className="flex flex-col">
          <div className="md:grid md:grid-cols-4 md:gap-4 space-y-4 md:space-y-0">
            <Sitemap
              title="Top Locations"
              contentList={region.topLocations.map(loc => ({
                title: loc.title,
                url: `${region.productionUrl}${loc.url}`,
              }))}
            />
            <Sitemap
              title={`Top Cities in ${region.countryName}`}
              contentList={region.topCities.map(city => ({
                title: city.title,
                url: `${region.productionUrl}${city.url}`,
              }))}
            />
            <Sitemap
              title="Top Countries"
              contentList={region.topCountries.map(country => ({
                title: country.title,
                url: `${region.productionUrl}${country.url}`,
              }))}
            />
          </div>
          <a href="https://www.europcar.com/en/locations" target="_blank" className="hover:underline text-green-600 font-bold mt-4 md:mt-0">
            All countries
          </a>
        </div>
      </div>

      <AppFooter />
    </>
  );
};

export default AI;
